import { Feature, FeatureGeometry } from '../models/Geoserver';
import area from '@turf/area';
import { featureCollection, polygon } from '@turf/helpers';
import polylabel from 'polylabel';
import inside from 'point-in-polygon-hao';
import tesselate from '@turf/tesselate';
import { LatLong } from '../models/Geometries';
import { MapRef } from 'react-map-gl';
import bbox from '@turf/bbox';

export const getTopRightCoordinate = (feature: Feature) => {
  let coords: number[][] = [[0, 0]];
  if (feature?.geometry?.type === 'MultiPolygon') {
    coords = feature.geometry.coordinates.flat(2);
  } else if (feature?.geometry?.type === 'Polygon') {
    coords = feature.geometry.coordinates.flat(1);
  } else if (feature?.geometry?.type === 'Point') {
    coords = [feature.geometry.coordinates];
  } else if (feature.geometry.type === 'MultiLineString') {
    coords = feature.geometry.coordinates[0];
  } else if (feature.geometry.type === 'LineString') {
    coords = feature.geometry.coordinates;
  }
  const maxLat = Math.max(...coords.map(c => c[1]));
  const pointsInMaxLat = coords.filter(c => c[1].toFixed(2) === maxLat.toFixed(2));
  if (pointsInMaxLat.length > 1) {
    const maxLong = Math.max(...pointsInMaxLat.map(c => c[0]));
    return pointsInMaxLat.filter(c => c[0] === maxLong)[0];
  }

  return pointsInMaxLat[0];

};

export const getPolygonCenter = (feature: Feature | undefined) => {
  if (!feature) return [0, 0];
  let maxAreaPolygon: number[][][] | undefined;

  if (feature.geometry.type === 'MultiPolygon') {
    let maxArea = 0;
    feature.geometry.coordinates.forEach((polygonCoords: number[][][]) => {
      const polygonArea = area(polygon(polygonCoords));
      if (polygonArea > maxArea) {
        maxArea = polygonArea;
        maxAreaPolygon = polygonCoords;
      }
    });
  } else if (feature.geometry.type === 'Polygon') {
    maxAreaPolygon = feature.geometry.coordinates;
  }

  if (maxAreaPolygon) {
    // uso la funcion polylabel para buscar un centro de manera rapida
    const polylabelPoint = polylabel(maxAreaPolygon, 10);
    // para poligonos concavos, polylabel puede llegar a ubicar el centro fuera del poligono.
    // en esos casos, uso tesselate para dividir el poligono en triangulos, busco el triangulo de mayor area
    // y uso el centro de ese triangulo
    if (inside(polylabelPoint, maxAreaPolygon)) {
      return polylabelPoint;
    } else {
      const triangles = tesselate(polygon(maxAreaPolygon));
      let maxTriangleArea = 0;
      let maxTriangle = triangles.features[0];
      triangles.features.forEach((triangle) => {
        const triangleArea = area(triangle);
        if (triangleArea > maxTriangleArea && inside(polylabel(triangle.geometry.coordinates), maxAreaPolygon!)) {
          maxTriangleArea = triangleArea;
          maxTriangle = triangle;
        }
      });
      return polylabel(maxTriangle.geometry.coordinates);
    }

  }


  return [0, 0];

};


export const getLineStringCenter = (feature: Feature | undefined) => {
  if (!feature) return [0, 0];

  let coords: number[][] = [];
  if (feature.geometry.type === 'MultiLineString') {
    coords = feature.geometry.coordinates[0];
  } else if (feature.geometry.type === 'LineString') {
    coords = feature.geometry.coordinates;
  }
  return coords[0];
};


export const generateBounds = (point1: LatLong, point2: LatLong): number[] => {
  const minLat = Math.min(point1.latitud, point2.latitud);
  const minLong = Math.min(point1.longitud, point2.longitud);
  const maxLat = Math.max(point1.latitud, point2.latitud);
  const maxLong = Math.max(point1.longitud, point2.longitud);
  return [minLong, minLat, maxLong, maxLat];
};

export const generateRectangleBounds = (coords: number[][][]): number[] => {
  return [coords[0][0][0], coords[0][2][1], coords[0][1][0], coords[0][0][1]];
};

let lastValidCoords: number[][] = [[]];

export const round5 = (x: number) => x;

export const generateRectangle = (point1: LatLong, point2: LatLong): FeatureGeometry => {
  const minLat = round5(Math.min(point1.latitud, point2.latitud));
  const minLong = round5(Math.min(point1.longitud, point2.longitud));
  const maxLat = round5(Math.max(point1.latitud, point2.latitud));
  const maxLong = round5(Math.max(point1.longitud, point2.longitud));
  return {
    type: 'Polygon',
    coordinates: [[
      [minLong, maxLat],
      [maxLong, maxLat],
      [maxLong, minLat],
      [minLong, minLat],
      [minLong, maxLat],
    ]],
  };
};


export const calculateArea = (coords: number[][]): number => {
  if (!coords) return 0;
  const line = polygon([[...coords, coords[0]]]);
  return area(line) / 1000000;
};


export const convertCoordinatesToEPSG3857 = (lon: number, lat: number) => {
  let x = (lon * 20037508.34) / 180;
  let y = Math.log(Math.tan(((90 + lat) * Math.PI) / 360)) / (Math.PI / 180);
  y = (y * 20037508.34) / 180;
  return [x, y];
};



export const centerMapOnGeometries = (mapRef: MapRef | undefined, geometries: FeatureGeometry[]) => {
  if(mapRef && geometries.length){
      const box = bbox(featureCollection(geometries.map(g => ({
        type: 'Feature',
        geometry: g,
        properties: {},
      }))));
      mapRef.fitBounds(
        box as any,
        {
          padding: {
            left: 440,
            right: 100,
            top: 32,
            bottom: 32
          },
          maxZoom: 13,
        },
      );

  }
};

export function degreesToDegreeMinuteSecond (coordinate: number, isLatitude: boolean, decimalPlaces = 5) {
  const direction = isLatitude ? (coordinate >= 0 ? "N" : "S") : (coordinate >= 0 ? "E" : "O");
  const degrees = Math.floor(Math.abs(coordinate));
  const minutesAndSeconds = (Math.abs(coordinate) - degrees) * 60;
  const minutes = Math.floor(minutesAndSeconds);
  const seconds = (minutesAndSeconds - minutes) * 60;

  return `${degrees}° ${minutes}' ${seconds.toFixed(decimalPlaces)}" ${direction}`;

}
